import { useState, useEffect } from "react";
import CompleteDropdown from "../../../../../layout/Dropdowns/CompleteDropdown";
import StatusTag, { statusConfig } from "./StatusTag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faTicket, faFile, faComment, faUser } from "@fortawesome/free-solid-svg-icons";
import { calculateDoneTicketPercentage } from "@serie3/freelancesplaceapi/Domain/Projects/Calculations";
import crudApi from "@serie3/common/api/crudApi";
import ProjectConfig from "@serie3/freelancesplaceapi/Config/Projects/ProjectConfig";

const getAccentColor = (start, end, done) => {
  const startDate = new Date(start).getTime();
  const endDate = new Date(end).getTime();
  const today = new Date().getTime();
  if (done) return statusConfig.done.color;
  if (startDate > today) return statusConfig.waiting.color;
  if (endDate < today) return statusConfig.notDone.color;
  return statusConfig.ongoing.color;
};

const fmt = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const MetaPill = ({ icon, value, color = "#9e9e9e" }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: "4px",
    fontSize: "0.75rem", color, fontWeight: 500,
  }}>
    <FontAwesomeIcon icon={icon} style={{ fontSize: "0.65rem" }} />
    {value}
  </span>
);

const getCustomerName = (project) => {
  const contact = project.person || project.customer;
  if (!contact) return null;
  if (Object.prototype.hasOwnProperty.call(contact, "anrede")) {
    return [contact.vorname, contact.name].filter(Boolean).join(" ");
  }
  return [contact.firmenname, contact.rechtsform].filter(Boolean).join(" ") || null;
};

const ProjectRow = ({ editAction, viewAction, deleteAction, project }) => {
  const [hovered, setHovered] = useState(false);
  const [customerName, setCustomerName] = useState(null);
  const [projectLogo, setProjectLogo] = useState(null);
  const [projectColor, setProjectColor] = useState(project.color || null);
  const accent = getAccentColor(project.start, project.end, project.done);

  useEffect(() => {
    crudApi.show(ProjectConfig.endpoints.show, project.id).then((fullProject) => {
      if (fullProject) {
        setCustomerName(getCustomerName(fullProject));
        if (fullProject.color) setProjectColor(fullProject.color);
        if (fullProject.file_name) {
          crudApi.getBinary("/project/filePreview/" + project.id).then((response) => {
            if (response?.data) setProjectLogo(URL.createObjectURL(response.data));
          });
        }
      }
    });
  }, [project.id]);
  const tickets = project.actions || [];
  const totalTickets = tickets.length;
  const doneTickets = tickets.filter(t => t.action?.status === "Done").length;
  const progress = totalTickets > 0 ? calculateDoneTicketPercentage(tickets) : null;

  const hasDocuments = project.documents?.length > 0;
  const hasComments = project.comments?.length > 0;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        borderRadius: "12px",
        boxShadow: hovered ? "0 4px 20px rgba(0,0,0,0.12)" : "0 1px 6px rgba(0,0,0,0.07)",
        borderLeft: `4px solid ${accent}`,
        padding: "14px 20px",
        display: "flex",
        alignItems: "center",
        gap: "16px",
        transition: "box-shadow 0.15s",
        cursor: "pointer",
      }}
      onClick={editAction}
    >
      {/* Logo / color avatar */}
      <div style={{
        width: "38px", height: "38px", borderRadius: "10px", flexShrink: 0,
        overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center",
        background: projectLogo ? "#f5f5f5" : (projectColor || accent),
        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      }}>
        {projectLogo
          ? <img src={projectLogo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          : <span style={{ color: "#fff", fontSize: "1rem", fontWeight: 800 }}>{project.title?.charAt(0)?.toUpperCase()}</span>
        }
      </div>

      {/* Title + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 700, fontSize: "0.97rem", color: "#212121",
          marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {project.title}
        </div>

        {/* Customer */}
        {customerName && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            fontSize: "0.78rem", fontWeight: 600, color: "#1a73e8",
            marginBottom: "4px",
          }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: "0.65rem" }} />
            {customerName}
          </div>
        )}

        {/* Dates + meta pills */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", color: "#9e9e9e", fontSize: "0.78rem" }}>
            <FontAwesomeIcon icon={faCalendarAlt} style={{ fontSize: "0.68rem" }} />
            {fmt(project.start)} → {fmt(project.end)}
          </span>

          {totalTickets > 0 && (
            <MetaPill icon={faTicket} value={`${doneTickets}/${totalTickets} tickets`} />
          )}
          {hasDocuments && (
            <MetaPill icon={faFile} value={project.documents.length} />
          )}
          {hasComments && (
            <MetaPill icon={faComment} value={project.comments.length} />
          )}
        </div>

        {/* Progress bar */}
        {progress !== null && (
          <div style={{ marginTop: "8px" }}>
            <div style={{
              height: "5px", borderRadius: "99px",
              background: "#f0f0f0", overflow: "hidden",
            }}>
              <div style={{
                height: "100%",
                width: `${progress}%`,
                background: progress === 100 ? "#2e7d32" : accent,
                borderRadius: "99px",
                transition: "width 0.4s ease",
              }} />
            </div>
            <div style={{ fontSize: "0.7rem", color: "#9e9e9e", marginTop: "3px" }}>
              {progress}% complete
            </div>
          </div>
        )}
      </div>

      {/* Status pill */}
      <div style={{ flexShrink: 0 }}>
        <StatusTag start={project.start} end={project.end} done={project.done} />
      </div>

      {/* Actions */}
      <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
        <CompleteDropdown
          editAction={editAction}
          deleteAction={deleteAction}
          viewAction={viewAction}
        />
      </div>
    </div>
  );
};

export default ProjectRow;
